import { useWorkspace } from '@/features/workspaces/hooks';
import { ConnectorFlowDTO, ConnectorInstanceSafeDTO, CustomConnectorDto } from '@/models/connectors';
import { useCompanyValues } from '@/providers';
import { useGetConnectorFlowsQuery, useGetConnectorInstancesQuery, useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { EMAIL_CONNECTOR_NAMES, EMAIL_FLOW_NAMES, type EmailFlowRole } from './constants';

type ResolveEmailChannelArgs = {
  connectors?: CustomConnectorDto[];
  instances?: ConnectorInstanceSafeDTO[];
  /** When set, prefer this connector over discovery. */
  preferredConnectorId?: string;
};

export const resolveEmailFlow = (flows: ConnectorFlowDTO[] | undefined, connectorId: string | undefined, role: EmailFlowRole) => {
  if (!connectorId) {
    return undefined;
  }
  const names: readonly string[] = EMAIL_FLOW_NAMES[role];
  return flows?.find((flow) => flow.connectorId === connectorId && names.includes(flow.name));
};

export const resolveEmailChannel = ({ connectors, instances, preferredConnectorId }: ResolveEmailChannelArgs) => {
  const preferred = preferredConnectorId ? connectors?.find((connector) => connector.id === preferredConnectorId) : undefined;
  const discovered = EMAIL_CONNECTOR_NAMES.map((name) => connectors?.find((connector) => connector.name === name && connector.isAuthenticated)).find(
    Boolean
  );
  const connector = preferred?.isAuthenticated ? preferred : discovered;

  return {
    connector,
    isAuthenticated: Boolean(connector?.isAuthenticated),
    instanceId: instances?.find((instance) => instance.connectorId === connector?.id)?.id,
  };
};

/**
 * Resolves the authenticated email connector for this workspace.
 */
export const useEmailChannelConnector = (options?: { connectorId?: string }) => {
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();

  const { data: connectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );

  const { data: instances, isLoading: isInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId: workspace.id }, { skip: !workspace.id });

  return {
    ...resolveEmailChannel({ connectors, instances, preferredConnectorId: options?.connectorId }),
    isLoading: isConnectorsLoading || isInstancesLoading,
  };
};

/**
 * The flow serving one role on the email connector, plus the instance to run it against.
 */
export const useEmailChannelFlow = (role: EmailFlowRole, options?: { connectorId?: string }) => {
  const workspace = useWorkspace();
  const channel = useEmailChannelConnector(options);

  const { data: flows, isLoading: isFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId: workspace.id, queryParams: { showUserJourney: true } },
    { skip: !workspace.id }
  );

  return {
    ...channel,
    flow: resolveEmailFlow(flows, channel.connector?.id, role),
    connectorInstanceId: channel.instanceId,
    isLoading: channel.isLoading || isFlowsLoading,
  };
};
